import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  Briefcase,
  ClipboardList,
  FileText,
  Send,
  Plus,
  DollarSign,
  ArrowUpRight,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API } from "../../lib/operatorWalk";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

const QUICK_ACTIONS = [
  { label: "New blog post", to: "/admin/blog", icon: Plus },
  { label: "New Operator Walk report", to: "/admin/operator-walk/reports", icon: Plus },
  { label: "New Operator Walk client", to: "/admin/operator-walk/clients", icon: Plus },
  { label: "View pricing", to: "/admin/pricing", icon: DollarSign },
];

export default function AdminHome() {
  const { authHeader } = usePortalAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestSending, setDigestSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/admin/dashboard`, { headers: authHeader });
      setData(res.data);
    } catch {
      toast.error("Could not load the dashboard.");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const runDigest = async () => {
    setDigestSending(true);
    try {
      const res = await axios.post(`${OW_API}/admin/digest/run?days=7`, {}, { headers: authHeader });
      const { total, sent } = res.data || {};
      toast.success(sent ? `Digest sent. ${total} unread from the past 7 days.` : "Digest generated but email send failed.");
    } catch {
      toast.error("Could not send the digest.");
    } finally {
      setDigestSending(false);
    }
  };

  const counts = data?.counts || {};
  const recent = data?.recent || {};

  const stats = [
    { label: "Active clients", value: counts.active_clients ?? 0, icon: Users, to: "/admin/operator-walk" },
    { label: "Unread contact", value: counts.unreviewed_contact ?? 0, icon: Briefcase, to: "/admin/contact" },
    { label: "Unread careers", value: counts.unreviewed_careers ?? 0, icon: Briefcase, to: "/admin/careers" },
    { label: "Unread assessments", value: counts.unreviewed_assessments ?? 0, icon: ClipboardList, to: "/admin/assessments" },
    { label: "Draft posts", value: counts.draft_posts ?? 0, icon: FileText, to: "/admin/blog" },
  ];

  return (
    <AdminLayout title="Dashboard" subtitle="A quick scan of everything that needs attention.">
      {loading ? (
        <div className="py-24 flex items-center justify-center text-ink-muted" data-testid="admin-home-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="space-y-10" data-testid="admin-home">
          {/* Counts */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-ink-line border border-ink-line">
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.label}
                  to={s.to}
                  className="bg-white p-5 lg:p-6 hover:bg-brand-50 transition-colors group"
                  data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-4 h-4 text-brand-400" />
                    <ArrowUpRight className="w-3.5 h-3.5 text-ink-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="font-serif text-4xl text-brand-700 leading-none mt-4">{s.value}</div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-ink-muted">{s.label}</div>
                </Link>
              );
            })}
          </div>

          {/* Quick actions + digest */}
          <section>
            <h2 className="font-serif text-2xl text-brand-700 mb-4">Quick actions</h2>
            <div className="flex flex-wrap gap-3">
              {QUICK_ACTIONS.map((a) => {
                const Icon = a.icon;
                return (
                  <Link
                    key={a.label}
                    to={a.to}
                    className="inline-flex items-center gap-2 bg-white border border-brand-200 hover:border-brand-500 text-brand-700 px-4 py-2.5 text-sm font-medium transition-colors"
                    data-testid={`admin-quick-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon className="w-4 h-4" /> {a.label}
                  </Link>
                );
              })}
              <button
                onClick={runDigest}
                disabled={digestSending}
                className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="admin-digest-send-now"
              >
                {digestSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send weekly digest now
              </button>
            </div>
          </section>

          {/* Recent activity */}
          <section>
            <h2 className="font-serif text-2xl text-brand-700 mb-4">Recent activity</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ActivityCard title="Latest blog posts" to="/admin/blog" empty="No posts yet." items={(recent.blog || []).map((p) => ({
                key: p.id, primary: p.title, secondary: p.status, when: p.updatedAt,
              }))} />
              <ActivityCard title="Latest Operator Walk reports" to="/admin/operator-walk/reports" empty="No published reports yet." items={(recent.reports || []).map((r) => ({
                key: r.id, primary: `${r.company_name} · ${r.location_name}`, secondary: r.month_label, when: r.published_at,
              }))} />
              <ActivityCard title="Latest career submissions" to="/admin/careers" empty="No career submissions yet." items={(recent.careers || []).map((s) => ({
                key: s.id, primary: s.name, secondary: s.read ? "read" : "unread", when: s.created_at,
              }))} />
              <ActivityCard title="Latest assessments" to="/admin/assessments" empty="No completed assessments yet." items={(recent.assessments || []).map((a) => ({
                key: a.id, primary: a.company || a.name, secondary: a.total50 != null ? `${a.total50}/50` : "", when: a.completedAt,
              }))} />
            </div>
          </section>
        </div>
      )}
    </AdminLayout>
  );
}

function ActivityCard({ title, to, items, empty }) {
  return (
    <div className="border border-ink-line bg-white" data-testid={`activity-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-line">
        <h3 className="text-sm font-medium text-brand-700">{title}</h3>
        <Link to={to} className="text-xs text-brand-500 hover:text-brand-700 inline-flex items-center gap-1">
          View all <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-sm text-ink-muted text-center">{empty}</div>
      ) : (
        <ul>
          {items.map((it) => (
            <li key={it.key} className="px-5 py-3.5 border-b border-ink-line last:border-b-0 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-brand-700 truncate">{it.primary}</div>
                {it.secondary && <div className="text-xs text-ink-muted">{it.secondary}</div>}
              </div>
              <div className="text-xs text-ink-muted shrink-0">{timeAgo(it.when)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
